import { useEffect, useState } from "react";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { CalendarDays, MapPin } from "lucide-react";
import { API } from "@/lib/content";
import { Reveal } from "./Reveal";
import { ReserveDialog } from "./ReserveDialog";

export const formatRange = (start, end) => {
  const s = parseISO(start);
  const e = parseISO(end);
  if (start === end) return format(s, "EEE, MMMM d, yyyy");
  const sameMonth = format(s, "yyyy-MM") === format(e, "yyyy-MM");
  return sameMonth ? `${format(s, "MMMM d")}–${format(e, "d, yyyy")}` : `${format(s, "MMM d")} – ${format(e, "MMM d, yyyy")}`;
};

const RetreatCard = ({ r, i, onReserved }) => (
  <Reveal delay={i * 100} className="bg-paper rounded-2xl p-8 flex flex-col" data-testid={`retreat-card-${r.id}`}>
    <p className="lb text-brass-ink flex items-center gap-2"><CalendarDays size={14} /> {formatRange(r.start_date, r.end_date)}</p>
    <h3 className="dp text-3xl mt-4">{r.title}</h3>
    <p className="mt-2 flex items-center gap-2 text-moss"><MapPin size={16} /> {r.location}</p>
    {r.description && <p className="mt-4 text-moss whitespace-pre-line">{r.description}</p>}
    <div className="mt-auto pt-8 flex items-center justify-between gap-4">
      <span className="text-sm tn" data-testid={`retreat-seats-left-${r.id}`}>
        {r.seats_left > 0 ? `${r.seats_left} ${r.seats_left === 1 ? "seat" : "seats"} left` : "Full"}
      </span>
      <ReserveDialog retreat={r} onReserved={onReserved} />
    </div>
  </Reveal>
);

export const Retreats = () => {
  const [items, setItems] = useState(null);
  const load = () => axios.get(`${API}/retreats`).then(({ data }) => setItems(data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  return (
    <section id="retreats" className="bg-sage text-pine py-24 md:py-32 scroll-mt-20" data-testid="retreats-section">
      <div className="max-w-6xl mx-auto px-6">
        <Reveal className="grid md:grid-cols-[1.2fr_1fr] gap-6 items-end">
          <h2 className="dp">Upcoming <em>weekend retreats</em></h2>
          <p className="text-moss md:pb-2">Reserving a seat is free. We'll follow up with you about the details.</p>
        </Reveal>
        <div className="mt-12 grid md:grid-cols-2 gap-6" data-testid="retreats-list">
          {items?.map((r, i) => <RetreatCard key={r.id} r={r} i={i} onReserved={load} />)}
          {items && items.length === 0 && (
            <div className="md:col-span-2 border border-dashed border-pine/30 rounded-2xl p-10" data-testid="retreats-empty">
              <p className="dp text-2xl">New retreat dates are coming soon.</p>
              <p className="mt-2 text-moss">
                Want to hear when they're announced? <a href="#contact" className="underline underline-offset-4 decoration-brass" data-testid="retreats-empty-contact-link">Send us a note</a>.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
